import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { platformAuthService } from '@/services/platform-auth.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { usePlatformAutoRefresh } from '@/hooks/use-platform-auto-refresh';

export function PlatformAuthInitializer({ children }: { children: React.ReactNode }) {
  const isInitialized = usePlatformAuthStore((s) => s.isInitialized);

  // Arms the proactive refresh timer for the current access token.
  // Must be called unconditionally (React hook rules) — the hook checks isAuthenticated
  // internally and does nothing when the user is not signed in.
  usePlatformAutoRefresh();

  useEffect(() => {
    const { isAuthenticated, accessToken, setInitialUser, clearAuth, setInitialized } =
      usePlatformAuthStore.getState();

    if (!isAuthenticated || !accessToken) {
      setInitialized();
      return;
    }

    // Validate the stored access token against the backend.
    // Uses platformApiClient which will silently refresh via the 401 interceptor if the
    // token has already expired — setInitialUser is still called after the retry succeeds.
    platformAuthService
      .getMe()
      .then((user) => {
        // setInitialUser only updates the user profile — tokens in the store are preserved
        // (the 401 interceptor may have already rotated them during getMe if needed).
        setInitialUser(user);
        setInitialized();
      })
      .catch(() => {
        clearAuth();
        setInitialized();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab session sync — see AuthInitializer for rationale. When the shared
  // `platform-auth` entry loses its access token in another tab (logout), this
  // tab drops its in-memory session and PlatformProtectedRoute redirects to login.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'platform-auth') return;
      const { isAuthenticated, clearAuth } = usePlatformAuthStore.getState();
      if (!isAuthenticated) return;

      let stillAuthed = false;
      try {
        if (e.newValue) {
          const parsed = JSON.parse(e.newValue) as { state?: { accessToken?: string | null } };
          stillAuthed = Boolean(parsed.state?.accessToken);
        }
      } catch {
        stillAuthed = false;
      }

      if (!stillAuthed) clearAuth();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
}
